import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { leadsAPI, usersAPI } from '../../services/api';
import LeadHistoryModalUser from './LeadHistoryModalUser';
import EditRequestModal from './EditRequestModal';
import { LeadStatusOptions } from '../../utils/leadUtils';

function LeadListing() {
  const { token } = useContext(AuthContext);
  const userId = localStorage.getItem('id');
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [totalPipelineValue, setTotalPipelineValue] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    source: 'all',
    assignee: 'all',
    dateFrom: '',
    dateTo: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [viewMode, setViewMode] = useState('table'); // Default to table for users
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddStatusModal, setShowAddStatusModal] = useState(false);
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [selectedSecondaryAssignees, setSelectedSecondaryAssignees] = useState([]);
  const [newStatusData, setNewStatusData] = useState({
    progressStatus: '',
    description: '', // Status change description
    plannedEndDate: '' // When this status should end
  });


  const fetchData = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const params = {
        ...filters,
        page: pagination.currentPage,
        limit: pagination.limit,
        view: viewMode
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all') {
          delete params[key];
        }
      });
      
      const [leadsResponse, usersResponse] = await Promise.all([
        leadsAPI.getAssignedLeads(userId, params),
        usersAPI.getPublicUsers().catch((err) => {
          console.warn('Failed to fetch users:', err.response?.data?.message || err.message);
          return { data: [] };
        }),
      ]);

      setLeads(leadsResponse.data.leads || leadsResponse.data);
      setFilteredLeads(leadsResponse.data.leads || leadsResponse.data);
      setTotalPipelineValue(leadsResponse.data.totalPipelineValue || 0);
      if (leadsResponse.data.pagination) {
        setPagination(prev => ({ ...prev, ...leadsResponse.data.pagination }));
      }
      setUsers(usersResponse.data);
      setError('');

      if (!usersResponse.data.length) {
        setError('Unable to fetch user data; usernames may not display correctly.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [userId, filters, pagination.currentPage, pagination.limit, viewMode]);

  useEffect(() => {
    if (token && userId) {
      fetchData();
    }
  }, [token, userId, fetchData]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      source: 'all',
      assignee: 'all',
      dateFrom: '',
      dateTo: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Reset to first page
  };


  const updateLead = async (leadId, historyId, updates) => {
    try {
      if (historyId) {
        await leadsAPI.updateLeadHistory(leadId, historyId, updates);
      } else {
        await leadsAPI.updateLead(leadId, updates);
      }

      setSuccess('Lead updated successfully!');
      setError('');

      await fetchData();

      if (selectedLead && selectedLead._id === leadId) {
        const updatedLead = leads.find((lead) => lead._id === leadId);
        setSelectedLead(updatedLead);
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update lead');
    }
  };

  const updateExpectedValue = async (leadId, expectedValue) => {
    try {
      await leadsAPI.updateExpectedValue(leadId, expectedValue);
      setSuccess('Expected value updated successfully!');
      setError('');
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update expected value');
    }
  };

  const addStatusAndDescription = async (leadId) => {
    if (!newStatusData.description.trim()) {
      setError('Description is required');
      return;
    }

    try {
      await leadsAPI.addLeadHistory(leadId, newStatusData);

      setSuccess('Status and description added successfully!');
      setError('');
      setShowAddStatusModal(false);
      setNewStatusData({ 
        progressStatus: '', 
        description: '',
        plannedEndDate: ''
      });
      
      await fetchData();

      if (selectedLead && selectedLead._id === leadId) {
        const updatedLead = leads.find((lead) => lead._id === leadId);
        setSelectedLead(updatedLead);
      }

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add status');
    }
  };

  const openHistoryModal = (lead) => {
    setSelectedLead(lead);
  };

  const closeHistoryModal = () => {
    setSelectedLead(null);
  };

  const openAddStatusModal = (lead) => {
    setSelectedLead(lead);
    setNewStatusData({ 
      progressStatus: lead.progressStatus, 
      description: '',
      plannedEndDate: ''
    });
    setShowAddStatusModal(true);
  };

  const closeAddStatusModal = () => {
    setShowAddStatusModal(false);
    setSelectedLead(null);
    setNewStatusData({ 
      progressStatus: '', 
      description: '',
      plannedEndDate: ''
    });
  };

  const updatePrimaryAssignee = async (leadId, primaryAssignee) => {
    setLoading(true);
    try {
      await leadsAPI.updateAssignees(leadId, { primaryAssignee });
      setSuccess('Primary assignee updated successfully!');
      setError('');
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update primary assignee');
    } finally {
      setLoading(false);
    }
  };

  const updateSecondaryAssignees = async (leadId, secondaryAssignees) => {
    setLoading(true);
    try {
      await leadsAPI.updateAssignees(leadId, { secondaryAssignees });
      setSuccess('Secondary assignees updated successfully!');
      setError('');
      await fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update secondary assignees');
    } finally {
      setLoading(false);
    }
  };

  const openAssigneeModal = (lead) => {
    setSelectedLead(lead);
    setSelectedSecondaryAssignees(lead.secondaryAssignees?.map(a => a._id) || []);
    setShowAssigneeModal(true);
  };

  const handleSecondaryAssigneeChange = (userId) => {
    setSelectedSecondaryAssignees(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const saveSecondaryAssignees = async () => {
    try {
      await updateSecondaryAssignees(selectedLead._id, selectedSecondaryAssignees);
      setShowAssigneeModal(false);
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-xl border border-gray-200/50">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">My Leads</h2>
          <p className="text-gray-600 mt-1">Manage and track your assigned leads</p>
        </div>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-3 rounded-xl border border-blue-200">
          <div className="text-sm font-medium text-blue-600">Total Assigned</div>
          <div className="text-2xl font-bold text-blue-900">{pagination.totalCount || 0}</div>
        </div>
      </div>
      
      {/* Filters Section */}
      <div className="mb-8 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6">
        <div className="flex items-center mb-6">
          <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900">Filter & Search My Leads</h3>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by school name..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
          >
            <option value="all">All Statuses</option>
            {LeadStatusOptions.map((status) => (
              <option key={status.value} value={status.value}>
                {status.name}
              </option>
            ))}
          </select>
          
          <select
            value={filters.source}
            onChange={(e) => handleFilterChange('source', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
          >
            <option value="all">All Sources</option>
            <option value="Website">Website</option>
            <option value="Event">Event</option>
            <option value="Referral">Referral</option>
            <option value="Direct Outreach">Direct Outreach</option>
            <option value="Marketing Campaign">Marketing Campaign</option>
          </select>
          
          <select
            value={filters.assignee}
            onChange={(e) => handleFilterChange('assignee', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
          >
            <option value="all">All Assignees</option>
            {users.map((user) => (
              <option key={user._id} value={user._id}>
                {user.username}
              </option>
            ))}
          </select>
          
          <select
            value={`${filters.sortBy}-${filters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('-');
              handleFilterChange('sortBy', sortBy);
              handleFilterChange('sortOrder', sortOrder);
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
          >
            <option value="createdAt-desc">Newest First</option>
            <option value="createdAt-asc">Oldest First</option>
            <option value="schoolName-asc">School Name A-Z</option>
            <option value="schoolName-desc">School Name Z-A</option>
          </select>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={clearFilters}
              className="bg-gray-500 text-white px-6 py-3 rounded-xl hover:bg-gray-600 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
            >
              Clear Filters
            </button>
            <button
              onClick={() => setViewMode(viewMode === 'table' ? 'card' : 'table')}
              className="bg-blue-500 text-white px-6 py-3 rounded-xl hover:bg-blue-600 transition-all duration-200 flex items-center gap-2 font-medium shadow-sm hover:shadow-md"
            >
              {viewMode === 'table' ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  Card View
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 6h18m-9 8h9" />
                  </svg>
                  Table View
                </>
              )}
            </button>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{pagination.totalCount}</div>
                <div className="text-sm text-gray-600">Total Leads</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{filteredLeads.length}</div>
                <div className="text-sm text-gray-600">On This Page</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">₹{totalPipelineValue.toFixed(0)}</div>
                <div className="text-sm text-gray-600">Pipeline Value</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {loading && <p className="text-center py-4">Loading...</p>}

      {/* Table/Card View Toggle */}
      {viewMode === 'table' ? (
        /* Table View */
        <div className="overflow-x-auto rounded-2xl border border-gray-200">
        <table className="w-full border-collapse bg-white">
          <thead>
            <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <th className="p-4 text-left font-semibold text-gray-900">School Name</th>
              <th className="p-4 text-left font-semibold text-gray-900">Contact Person</th>
              <th className="p-4 text-left font-semibold text-gray-900">Address</th>
              <th className="p-4 text-left font-semibold text-gray-900">Phone</th>
              <th className="p-4 text-left font-semibold text-gray-900">Email</th>
              <th className="p-4 text-left font-semibold text-gray-900">Status</th>
              <th className="p-4 text-left font-semibold text-gray-900">Source</th>
              <th className="p-4 text-left font-semibold text-gray-900">Expected Value</th>
              <th className="p-4 text-left font-semibold text-gray-900">Assignees</th>
              <th className="p-4 text-left font-semibold text-gray-900">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.map((lead) => (
              <tr key={lead._id} className="border-b border-gray-100 hover:bg-blue-50/50 transition-colors">
                <td className="p-4 font-medium text-gray-900">{lead.schoolName}</td>
                <td className="p-4">
                  {lead.personName ? (
                    <div>
                      <div className="font-medium text-gray-900">{lead.personName}</div>
                      {lead.designation && <div className="text-xs text-gray-500">{lead.designation}</div>}
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-4 text-gray-700">{lead.address}</td>
                <td className="p-4 text-gray-700">{lead.phoneNumber}</td>
                <td className="p-4 text-gray-700">{lead.email}</td>
                <td className="p-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    lead.progressStatus === 'Closed Won' ? 'bg-green-100 text-green-800' :
                    lead.progressStatus === 'Closed Lost' ? 'bg-red-100 text-red-800' :
                    lead.progressStatus === 'Cold Lead' ? 'bg-gray-100 text-gray-800' :
                    lead.progressStatus === 'Prospecting' ? 'bg-blue-100 text-blue-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {lead.progressStatus || 'N/A'}
                  </span>
                </td>
                <td className="p-3">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                    {lead.source}
                  </span>
                </td>
                <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <label className="text-xs font-medium text-gray-700"> Value</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={lead.expectedValue || 0}
                        onChange={(e) => updateExpectedValue(lead._id, parseFloat(e.target.value) || 0)}
                        className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-20"
                      />
                    </div>
                </td>
                <td className="p-3">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <label className="text-xs font-medium text-gray-700">Primary:</label>
                      <select
                        value={lead.primaryAssignee?._id || ''}
                        onChange={(e) => updatePrimaryAssignee(lead._id, e.target.value)}
                        className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 flex-1"
                      >
                        <option value="">None</option>
                        {users.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.username}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-xs font-medium text-gray-700">Secondary:</label>
                      <button
                        onClick={() => openAssigneeModal(lead)}
                        className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition flex-1"
                      >
                        Manage ({lead.secondaryAssignees?.length || 0})
                      </button>
                    </div>
                  </div>
                </td>
                <td className="p-3">
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => openHistoryModal(lead)}
                          className="bg-green-500 text-white px-3 py-1 rounded-lg hover:bg-green-600 transition text-sm"
                        >
                          View History
                        </button>
                        <button
                          onClick={() => openAddStatusModal(lead)}
                          className="bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600 transition text-sm"
                        >
                          Add Update
                        </button>
                        <button
                          onClick={() => {
                            setSelectedLead(lead);
                            setShowEditRequestModal(true);
                          }}
                          className="bg-orange-500 text-white px-3 py-1 rounded-lg hover:bg-orange-600 transition text-sm"
                        >
                          Request Edit
                        </button>
                      </div>
                    </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        /* Card View */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredLeads.map((lead) => (
          <div
            key={lead._id}
            className="border border-gray-200 rounded-2xl p-6 shadow-sm bg-white hover:shadow-lg transition-all duration-200 hover:border-blue-200"
          >
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">{lead.schoolName}</h3>
                  {lead.personName && (
                    <p className="text-sm text-gray-700 font-medium mt-1">
                      {lead.personName} {lead.designation && `(${lead.designation})`}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">{lead.address}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase">Phone</span>
                  <p className="text-sm text-gray-700 mt-1">{lead.phoneNumber}</p>
                </div>

                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase">Email</span>
                  <p className="text-sm text-gray-700 mt-1 break-all">{lead.email}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase">Status</span>
                  <p className="mt-1">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium inline-block ${
                      lead.progressStatus === 'Closed Won' ? 'bg-green-100 text-green-800' :
                      lead.progressStatus === 'Closed Lost' ? 'bg-red-100 text-red-800' :
                      lead.progressStatus === 'Cold Lead' ? 'bg-gray-100 text-gray-800' :
                      lead.progressStatus === 'Prospecting' ? 'bg-blue-100 text-blue-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {lead.progressStatus || 'N/A'}
                    </span>
                  </p>
                </div>

                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase">Source</span>
                  <p className="mt-1">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded inline-block">
                      {lead.source}
                    </span>
                  </p>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <div>
                  <span className="text-xs font-semibold text-gray-500 uppercase">Assignees</span>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center space-x-2">
                      <label className="text-xs font-medium text-gray-700">Primary:</label>
                      <select
                        value={lead.primaryAssignee?._id || ''}
                        onChange={(e) => updatePrimaryAssignee(lead._id, e.target.value)}
                        className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 flex-1"
                      >
                        <option value="">None</option>
                        {users.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.username}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-xs font-medium text-gray-700">Secondary:</label>
                      <button
                        onClick={() => openAssigneeModal(lead)}
                        className="text-xs px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition flex-1"
                      >
                        Manage ({lead.secondaryAssignees?.length || 0})
                      </button>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-xs font-medium text-gray-700">Expected Value:</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={lead.expectedValue || 0}
                    onChange={(e) => updateExpectedValue(lead._id, parseFloat(e.target.value) || 0)}
                    className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-24"
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => openHistoryModal(lead)}
                      className="w-full bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
                    >
                      View History
                    </button>
                    <button
                      onClick={() => openAddStatusModal(lead)}
                      className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
                    >
                      Add Update
                    </button>
                    <button
                      onClick={() => {
                        setSelectedLead(lead);
                        setShowEditRequestModal(true);
                      }}
                      className="w-full bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition"
                    >
                      Request Edit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && pagination.totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-6">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPrev}
            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
            const pageNum = Math.max(1, pagination.currentPage - 2) + i;
            if (pageNum > pagination.totalPages) return null;
            
            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`px-3 py-2 rounded-lg border ${
                  pageNum === pagination.currentPage
                    ? 'bg-blue-500 text-white border-blue-500'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
          
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNext}
            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredLeads.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-lg mt-4">No leads found.</p>
          <p className="text-sm mt-2">Try adjusting your filters or check back later for new leads.</p>
        </div>
      )}

      {/* History Modal */}
      {selectedLead && !showAddStatusModal && (
        <LeadHistoryModalUser
          lead={selectedLead}
          onClose={closeHistoryModal}
          users={users}
          currentUserId={userId}
          onUpdateLead={updateLead}
        />
      )}

      {/* Add Status Modal */}
      {showAddStatusModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Add Status Update</h3>
              <button
                onClick={closeAddStatusModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Lead: <span className="font-semibold">{selectedLead.schoolName}</span>
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Status (Optional)
                </label>
                <select
                  value={newStatusData.progressStatus}
                  onChange={(e) => setNewStatusData({ ...newStatusData, progressStatus: e.target.value })}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Keep current status</option>
                  {LeadStatusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status Planned End Date
                </label>
                {/* When the current progress status should end */}
                <input
                  type="date"
                  value={newStatusData.plannedEndDate}
                  onChange={(e) => setNewStatusData({ ...newStatusData, plannedEndDate: e.target.value })}
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500 ml-1" title="This field is required">
                    <svg className="w-3 h-3 inline-block" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </span>
                </label>
                <textarea
                  value={newStatusData.description}
                  onChange={(e) => setNewStatusData({ ...newStatusData, description: e.target.value })}
                  placeholder="Enter update details..."
                  className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-32"
                  required
                />
                {!newStatusData.description.trim() && (
                  <p className="text-red-500 text-xs mt-1">This field is required</p>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => addStatusAndDescription(selectedLead._id)}
                  className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
                >
                  Add Update
                </button>
                <button
                  onClick={closeAddStatusModal}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Request Modal */}
      {showEditRequestModal && selectedLead && (
        <EditRequestModal
          lead={selectedLead}
          onClose={() => setShowEditRequestModal(false)}
          onSuccess={() => {
            setShowEditRequestModal(false);
            setSuccess('Edit request submitted successfully!');
            setTimeout(() => setSuccess(''), 3000);
          }}
        />
      )}
      
      {/* Secondary Assignee Modal */}
      {showAssigneeModal && selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Manage Secondary Assignees</h3>
              <button
                onClick={() => setShowAssigneeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
              {users.filter(user => user._id !== selectedLead.primaryAssignee?._id).map((user) => (
                <label key={user._id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    checked={selectedSecondaryAssignees.includes(user._id)}
                    onChange={() => handleSecondaryAssigneeChange(user._id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">{user.username}</span>
                </label>
              ))}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={saveSecondaryAssignees}
                disabled={loading}
                className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setShowAssigneeModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadListing;